import React from 'react';
import PropTypes from 'prop-types';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Collapse, Fade, Stack, Tooltip, Typography, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import { PULSE_V2_NEUTRAL_SURFACE_BG, WHITE_PILL_SEGMENTED_TOGGLE_SX } from './control_language_styles';

const VIEW_OPTIONS = [
  { value: 'average', label: 'Average' },
  { value: 'distribution', label: 'Distribution' },
  { value: 'percent_positive', label: '% Positive' },
];

export const DEFAULT_VIEW_MODE = 'average';

export const THEMES = {
  original: { scale: ['#94A3B8', '#CBD5E1', '#E2E8F0', '#93C5FD', '#2563EB'], metric: '#2563EB' },
  refined_blue: { scale: ['#94A3B8', '#CBD5E1', '#E2E8F0', '#93C5FD', '#60A5FA'], metric: '#60A5FA' },
  warm_cool: { scale: ['#F87171', '#FDBA74', '#E2E8F0', '#6EE7B7', '#34D399'], metric: '#34D399' },
  soft_diverging: { scale: ['#FCA5A5', '#FDE68A', '#E2E8F0', '#A7F3D0', '#6EE7B7'], metric: '#6EE7B7' },
  care: { scale: ['#E36464', '#FC9586', '#F2CA68', '#FFEE7D', '#97C284'], metric: '#97C284' },
  tol_blue_red: { scale: ['#B2182B', '#D6604D', '#F7F7F7', '#4393C3', '#2166AC'], metric: '#2166AC' },
  sunset: { scale: ['#364B9A', '#6EA6CD', '#EAECCC', '#FDB366', '#A50026'], metric: '#A50026' },
  us_federal: { scale: ['#853A22', '#FFBEA9', '#E8E8E8', '#97BCE9', '#112E51'], metric: '#112E51' },
  earth_tones: { scale: ['#A6611A', '#DFC27D', '#F5F5F5', '#80CDC1', '#018571'], metric: '#018571' },
};

export const DEFAULT_THEME_KEY = 'refined_blue';
const ANONYMITY_MESSAGE = 'To protect anonymity, results are hidden when fewer than 5 participants responded.';
const GROUPED_DOMAIN_FALLBACK = 'Uncategorized';
const LEGEND_BAR_END_RIGHT_OFFSET = '96px';
const LEGEND_HORIZONTAL_PADDING = '20px';
const GROUP_MODE_TRANSITION_EXIT_MS = 140;
const GROUP_MODE_TRANSITION_ENTER_MS = 220;
const GROUP_MODE_TRANSITION_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

const SCALE_LEGEND_ENTRY_PROP_TYPE = PropTypes.shape({
  score: PropTypes.number,
  order: PropTypes.number,
  label: PropTypes.string,
});

const formatPercent = (value) => `${Number(value || 0).toFixed(0)}%`;
const formatCount = (value) => `N=${value || 0}`;
const formatAverage = (value) => Number(value || 0).toFixed(1);

const META_TEXT_SX = {
  letterSpacing: 0.3,
  color: '#94a3b8',
  fontWeight: 600,
};

const clamp = (value, min = 0, max = 100) => Math.min(max, Math.max(min, value));

const averageToPercent = (average, totalResponses = 0) => {
  if (!totalResponses || average <= 0) {
    return 0;
  }

  return clamp(((average - 1) / 4) * 100);
};

const resolveGroupedQuestionHeaderMetric = (rows, viewMode) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const totalResponses = normalizedRows.reduce((sum, row) => sum + Number(row?.n || 0), 0);
  if (totalResponses <= 0) {
    return null;
  }

  if (viewMode === 'percent_positive') {
    const weightedPercentPositive =
      normalizedRows.reduce((sum, row) => sum + Number(row?.percent_positive || 0) * Number(row?.n || 0), 0) / totalResponses;
    return {
      label: '% Positive',
      value: formatPercent(weightedPercentPositive),
    };
  }

  if (viewMode === 'average') {
    const weightedAverage = normalizedRows.reduce((sum, row) => sum + Number(row?.average || 0) * Number(row?.n || 0), 0) / totalResponses;
    return {
      label: 'Average',
      value: formatAverage(weightedAverage),
    };
  }

  return null;
};

const resolveGroupedQuestionHeaderMetricFromSummary = (summary, viewMode) => {
  if (!summary || summary.total_responses <= 0) return null;
  if (viewMode === 'percent_positive') return { label: '% Positive', value: formatPercent(summary.percent_positive) };
  if (viewMode === 'average') return { label: 'Average', value: formatAverage(summary.average) };
  return null;
};

const resolveGroupedDomainHeaderMetric = (questions, viewMode) => {
  if (viewMode === 'distribution') {
    return null;
  }

  const normalizedQuestions = Array.isArray(questions) ? questions : [];
  const hasSummaries = normalizedQuestions.some((q) => q?.summary);
  if (hasSummaries) {
    const questionsWithSummary = normalizedQuestions.filter((q) => q?.summary && q.summary.total_responses > 0);
    const totalResponses = questionsWithSummary.reduce((sum, q) => sum + Number(q.summary.total_responses || 0), 0);
    if (totalResponses <= 0) return null;

    if (viewMode === 'percent_positive') {
      const weighted =
        questionsWithSummary.reduce((sum, q) => sum + Number(q.summary.percent_positive || 0) * Number(q.summary.total_responses || 0), 0) /
        totalResponses;
      return { label: '% Positive', value: formatPercent(weighted) };
    }
    const weighted =
      questionsWithSummary.reduce((sum, q) => sum + Number(q.summary.average || 0) * Number(q.summary.total_responses || 0), 0) / totalResponses;
    return { label: 'Average', value: formatAverage(weighted) };
  }

  const domainRows = normalizedQuestions.flatMap((question) => (Array.isArray(question?.rows) ? question.rows : []));
  const totalResponses = domainRows.reduce((sum, row) => sum + Number(row?.n || 0), 0);
  if (totalResponses <= 0) {
    return null;
  }

  if (viewMode === 'percent_positive') {
    const weightedPercentPositive =
      domainRows.reduce((sum, row) => sum + Number(row?.percent_positive || 0) * Number(row?.n || 0), 0) / totalResponses;
    return {
      label: '% Positive',
      value: formatPercent(weightedPercentPositive),
    };
  }

  const weightedAverage = domainRows.reduce((sum, row) => sum + Number(row?.average || 0) * Number(row?.n || 0), 0) / totalResponses;
  return {
    label: 'Average',
    value: formatAverage(weightedAverage),
  };
};

const resolveUngroupedDomainHeaderMetric = (summary, viewMode) => {
  if (viewMode === 'distribution') {
    return null;
  }

  const totalResponses = Number(summary?.total_responses || 0);
  if (totalResponses <= 0) {
    return null;
  }

  if (viewMode === 'percent_positive') {
    return {
      value: formatPercent(Number(summary?.percent_positive || 0)),
    };
  }

  return {
    value: formatAverage(Number(summary?.average || 0)),
  };
};

const getSortMetric = (viewMode) => (viewMode === 'average' ? 'average' : 'percent_positive');

const sortRows = (rows, sortOrder, viewMode) => {
  if (sortOrder === 'default' || !Array.isArray(rows)) return rows;
  const metric = getSortMetric(viewMode);
  const direction = sortOrder === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (a.suppressed && !b.suppressed) return 1;
    if (!a.suppressed && b.suppressed) return -1;
    const diff = Number(a[metric] || 0) - Number(b[metric] || 0);
    if (diff !== 0) return direction * diff;
    return Number(b.n || 0) - Number(a.n || 0);
  });
};

const sortUngroupedQuestions = (questions, sortOrder, viewMode) => {
  if (sortOrder === 'default' || !Array.isArray(questions)) return questions;
  const metric = getSortMetric(viewMode);
  const direction = sortOrder === 'desc' ? -1 : 1;
  return [...questions].sort((a, b) => {
    if (a.base?.suppressed && !b.base?.suppressed) return 1;
    if (!a.base?.suppressed && b.base?.suppressed) return -1;
    const diff = Number(a.base?.[metric] || 0) - Number(b.base?.[metric] || 0);
    if (diff !== 0) return direction * diff;
    return Number(b.base?.total_responses || 0) - Number(a.base?.total_responses || 0);
  });
};

const resolveGroupedQuestionMetricForSort = (question, viewMode) => {
  if (question?.summary) return resolveGroupedQuestionHeaderMetricFromSummary(question.summary, viewMode);
  return resolveGroupedQuestionHeaderMetric(question?.rows, viewMode);
};

const sortGroupedQuestions = (questions, sortOrder, viewMode, questionOrderInDomain) => {
  if (sortOrder === 'default') {
    return [...questions].sort((a, b) => {
      const ai = questionOrderInDomain[`${a?.question_id ?? ''}`] ?? Infinity;
      const bi = questionOrderInDomain[`${b?.question_id ?? ''}`] ?? Infinity;
      return ai - bi;
    });
  }
  const direction = sortOrder === 'desc' ? -1 : 1;
  return [...questions].sort((a, b) => {
    const aMetric = resolveGroupedQuestionMetricForSort(a, viewMode);
    const bMetric = resolveGroupedQuestionMetricForSort(b, viewMode);
    const aValue = aMetric ? parseFloat(aMetric.value) || 0 : 0;
    const bValue = bMetric ? parseFloat(bMetric.value) || 0 : 0;
    return direction * (aValue - bValue);
  });
};

const sortUngroupedSections = (sections, sortOrder, viewMode) => {
  if (sortOrder === 'default' || !Array.isArray(sections)) return sections;
  const direction = sortOrder === 'desc' ? -1 : 1;
  return [...sections].sort((a, b) => {
    const aValue = Number(a?.summary?.[getSortMetric(viewMode)] || 0);
    const bValue = Number(b?.summary?.[getSortMetric(viewMode)] || 0);
    return direction * (aValue - bValue);
  });
};

const getGroupedDomainSortValue = (questions, viewMode) => {
  const headerMetric = resolveGroupedDomainHeaderMetric(questions, viewMode);
  return headerMetric ? parseFloat(headerMetric.value) || 0 : 0;
};

const normalizeDistribution = (distribution) => {
  const values = Array.isArray(distribution) ? distribution : [];
  const normalized = [0, 0, 0, 0, 0].map((_, index) => Number(values[index] || 0));
  const total = normalized.reduce((sum, value) => sum + value, 0);
  if (total <= 0) {
    return normalized;
  }
  return normalized.map((value) => (value * 100) / total);
};

const normalizeScaleLegend = (scaleLegend) => {
  const entries = Array.isArray(scaleLegend) ? scaleLegend : [];
  return entries
    .map((entry, index) => {
      const score = Number(entry?.score) || index + 1;
      const order = Number(entry?.order) || score;
      const label = `${entry?.label || ''}`.trim() || `${score}`;
      return { score, order, label };
    })
    .sort((left, right) => left.order - right.order || left.score - right.score);
};

const scaleColor = (scaleColors, score, fallbackIndex = 0) => {
  const scoreIndex = Number(score) - 1;
  return scaleColors[scoreIndex] || scaleColors[fallbackIndex] || scaleColors[scaleColors.length - 1];
};

const DistributionBar = ({ distribution, scaleColors, scaleLegend }) => {
  const normalized = normalizeDistribution(distribution);
  const hasValues = normalized.some((value) => value > 0);
  const normalizedLegend = normalizeScaleLegend(scaleLegend);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'), { noSsr: true });

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.25 }}>
      {normalized.map((value, index) => {
        const entry = normalizedLegend[index];
        const label = entry?.label || `${index + 1}`;
        const color = scaleColor(scaleColors, entry?.score || index + 1, index);
        return (
          <Stack key={index} direction="row" alignItems="center" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ flex: 1, fontSize: '11px', fontWeight: 400, color: 'inherit', lineHeight: 1.3 }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 500, color: 'inherit', lineHeight: 1.3 }}>
              {`${Number(value).toFixed(1)}%`}
            </Typography>
          </Stack>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Tooltip
        title={isDesktop && hasValues ? tooltipContent : ''}
        arrow
        placement="top"
        enterDelay={400}
        enterNextDelay={200}
        leaveDelay={80}
        slotProps={{
          tooltip: { sx: { backgroundColor: 'rgba(33, 33, 33, 0.92)', px: 1.5, py: 1, maxWidth: 350 } },
        }}
      >
        <Box
          sx={{
            borderRadius: 0,
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            display: 'flex',
            height: 27,
            backgroundColor: PULSE_V2_NEUTRAL_SURFACE_BG,
          }}
        >
          {hasValues ? (
            normalized.map((value, index) => (
              <Box
                key={index}
                sx={{
                  width: `${value}%`,
                  minWidth: value > 0 ? '2px' : 0,
                  backgroundColor: scaleColor(scaleColors, index + 1, index),
                  borderRight: '1px solid rgba(255, 255, 255, 0.55)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 0.35,
                  '&:last-of-type': {
                    borderRight: 0,
                  },
                }}
              >
                {value >= 5 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#000000',
                      fontSize: '9px',
                      fontWeight: 400,
                      textShadow: 'none',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}
                  >
                    {`${Number(value || 0).toFixed(1)}%`}
                  </Typography>
                )}
              </Box>
            ))
          ) : (
            <Box
              sx={{
                width: '100%',
                backgroundColor: PULSE_V2_NEUTRAL_SURFACE_BG,
                height: '100%',
              }}
            />
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

DistributionBar.propTypes = {
  distribution: PropTypes.arrayOf(PropTypes.number),
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  scaleLegend: PropTypes.arrayOf(SCALE_LEGEND_ENTRY_PROP_TYPE),
};

DistributionBar.defaultProps = {
  scaleLegend: [],
};

const MetricBar = ({ percent, metricColor }) => (
  <Box sx={{ width: '100%' }}>
    <Box
      sx={{
        borderRadius: 0,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        height: 27,
        backgroundColor: PULSE_V2_NEUTRAL_SURFACE_BG,
      }}
    >
      <Box sx={{ width: `${clamp(percent || 0)}%`, height: '100%', backgroundColor: metricColor }} />
    </Box>
  </Box>
);

MetricBar.propTypes = {
  percent: PropTypes.number,
  metricColor: PropTypes.string.isRequired,
};

MetricBar.defaultProps = {
  percent: 0,
};

const Legend = ({ scaleLegend, scaleColors }) => {
  const normalizedLegend = normalizeScaleLegend(scaleLegend);
  if (normalizedLegend.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        pl: LEGEND_HORIZONTAL_PADDING,
        pr: { xs: LEGEND_HORIZONTAL_PADDING, md: LEGEND_BAR_END_RIGHT_OFFSET },
        py: 1.35,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 0.75, md: 2.5 }}
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        flexWrap="wrap"
      >
        {normalizedLegend.map((entry, index) => (
          <Stack key={`${entry.score}-${entry.label}`} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: scaleColor(scaleColors, entry.score, index) }} />
            <Typography variant="caption" sx={{ letterSpacing: 0.25, color: '#64748b', fontWeight: 400, fontSize: '12.5px' }}>
              {entry.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

Legend.propTypes = {
  scaleLegend: PropTypes.arrayOf(SCALE_LEGEND_ENTRY_PROP_TYPE),
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
};

Legend.defaultProps = {
  scaleLegend: [],
};

const LikertEmptyState = ({ message }) => (
  <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 2.2, backgroundColor: '#ffffff', p: 3 }}>
    <Stack alignItems="center" justifyContent="center" spacing={1.4} sx={{ minHeight: 136, textAlign: 'center' }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#94a3b8',
        }}
      >
        <InsertChartOutlinedIcon sx={{ fontSize: 30 }} />
      </Box>
      <Typography variant="body2" sx={{ color: '#64748b' }}>
        {message}
      </Typography>
    </Stack>
  </Box>
);

LikertEmptyState.propTypes = {
  message: PropTypes.string,
};

LikertEmptyState.defaultProps = {
  message: 'No responses found for the selected filters.',
};

const QuestionRow = ({ question, viewMode, scaleColors, metricColor }) => {
  const totalResponses = question.base?.total_responses || 0;

  if (question.base?.suppressed) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '41% 1fr 64px' },
          alignItems: 'center',
          columnGap: { xs: 1.5, md: 1.6 },
          rowGap: { xs: 1.2, md: 0.8 },
          py: { xs: 1.5, md: 1.75 },
          px: 2.5,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 400, fontSize: '1rem', lineHeight: 1.35, color: '#334155' }}>
          {question.question}
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.875rem', lineHeight: 1.4 }}>
          {ANONYMITY_MESSAGE}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <Typography variant="caption" sx={{ ...META_TEXT_SX, letterSpacing: 0, fontWeight: 400 }}>
            {formatCount(totalResponses)}
          </Typography>
        </Box>
      </Box>
    );
  }

  const average = Number(question.base?.average || 0);
  const percentPositive = Number(question.base?.percent_positive || 0);
  const averagePercent = averageToPercent(average, totalResponses);
  const hasResponses = totalResponses > 0;
  const isDistributionView = viewMode === 'distribution';
  const metricValue = !hasResponses ? '—' : viewMode === 'percent_positive' ? formatPercent(percentPositive) : formatAverage(average);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '41% 1fr 64px' },
        alignItems: 'center',
        columnGap: { xs: 1.5, md: 1.6 },
        rowGap: { xs: 1.2, md: 0.8 },
        py: { xs: 1.5, md: 1.75 },
        px: 2.5,
        borderTop: '1px solid #e2e8f0',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 400,
          fontSize: { xs: '1rem', md: '1rem' },
          lineHeight: 1.35,
          color: '#334155',
        }}
      >
        {question.question}
      </Typography>
      <Box>
        {viewMode === 'distribution' && (
          <DistributionBar distribution={question.base?.distribution} scaleColors={scaleColors} scaleLegend={question.base?.scale_legend} />
        )}
        {viewMode === 'percent_positive' && <MetricBar percent={percentPositive} metricColor={metricColor} />}
        {viewMode === 'average' && <MetricBar percent={averagePercent} metricColor={metricColor} />}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
        {isDistributionView ? (
          <Typography
            variant="caption"
            sx={{ ...META_TEXT_SX, letterSpacing: 0, textAlign: { xs: 'left', md: 'right' }, fontWeight: 400, mr: { xs: 0, md: '6px' } }}
          >
            {formatCount(totalResponses)}
          </Typography>
        ) : hasResponses ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
            <Typography
              variant="body2"
              sx={{
                minWidth: 48,
                px: 1.0,
                py: 0.2,
                textAlign: 'center',
                borderRadius: 2,
                backgroundColor: '#e2e8f0',
                color: '#334155',
                fontWeight: 700,
                fontSize: '1.05rem',
                lineHeight: 1.25,
                letterSpacing: 0.3,
              }}
            >
              {metricValue}
            </Typography>
            <Typography variant="caption" sx={{ ...META_TEXT_SX, letterSpacing: 0, fontWeight: 400, mt: 0.35 }}>
              {formatCount(totalResponses)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ ...META_TEXT_SX, letterSpacing: 0, textAlign: { xs: 'left', md: 'right' }, fontWeight: 400 }}>
            {metricValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

QuestionRow.propTypes = {
  question: PropTypes.shape({
    question: PropTypes.string,
    base: PropTypes.shape({
      distribution: PropTypes.arrayOf(PropTypes.number),
      total_responses: PropTypes.number,
      average: PropTypes.number,
      percent_positive: PropTypes.number,
      scale_legend: PropTypes.arrayOf(SCALE_LEGEND_ENTRY_PROP_TYPE),
    }),
  }).isRequired,
  viewMode: PropTypes.oneOf(['average', 'percent_positive', 'distribution']).isRequired,
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  metricColor: PropTypes.string.isRequired,
};

const DomainCard = ({ section, viewMode, sortOrder, scaleColors, metricColor }) => {
  const domainLegend = section.questions[0]?.base?.scale_legend || [];
  const domainHeaderMetric = resolveUngroupedDomainHeaderMetric(section.summary, viewMode);
  const [expanded, setExpanded] = React.useState(true);
  const sortedQuestions = React.useMemo(
    () => (viewMode === 'distribution' ? section.questions : sortUngroupedQuestions(section.questions, sortOrder, viewMode)),
    [section.questions, sortOrder, viewMode]
  );

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 2.2,
        overflow: 'hidden',
        mb: 3,
        backgroundColor: '#ffffff',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        sx={{
          px: 2.5,
          py: 1.9,
          backgroundColor: PULSE_V2_NEUTRAL_SURFACE_BG,
          borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: '#1e293b',
            fontSize: '1rem',
            lineHeight: 1.3,
          }}
        >
          {section.domain}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexShrink: 0 }}>
          {domainHeaderMetric && (
            <Typography
              variant="body2"
              sx={{
                color: '#334155',
                fontWeight: 400,
                fontSize: { xs: '0.95rem', md: '1rem' },
                lineHeight: 1.3,
                letterSpacing: 0,
                flexShrink: 0,
              }}
            >
              <Box component="span" sx={{ fontSize: { xs: '0.825rem', md: '0.875rem' }, fontWeight: 400, mr: 0.7 }}>
                Domain Overall
              </Box>
              <Box component="span" sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, fontWeight: 700, color: '#1e293b' }}>
                {domainHeaderMetric.value}
              </Box>
            </Typography>
          )}
          <KeyboardArrowDownIcon
            sx={{
              color: '#94a3b8',
              fontSize: 20,
              transform: expanded ? 'rotate(0deg)' : 'rotate(-180deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </Box>
      </Stack>
      <Collapse in={expanded} unmountOnExit timeout={180}>
        <Box>
          {sortedQuestions.map((question) => (
            <QuestionRow
              key={`${section.domain}-${question.question_id}`}
              question={question}
              viewMode={viewMode}
              scaleColors={scaleColors}
              metricColor={metricColor}
            />
          ))}
          {domainLegend.length > 0 && (
            <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
              <Legend scaleLegend={domainLegend} scaleColors={scaleColors} />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

DomainCard.propTypes = {
  section: PropTypes.shape({
    domain: PropTypes.string.isRequired,
    summary: PropTypes.shape({
      average: PropTypes.number,
      percent_positive: PropTypes.number,
      total_responses: PropTypes.number,
      question_count: PropTypes.number,
    }),
    questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  viewMode: PropTypes.oneOf(['average', 'percent_positive', 'distribution']).isRequired,
  sortOrder: PropTypes.oneOf(['default', 'desc', 'asc']),
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  metricColor: PropTypes.string.isRequired,
};

DomainCard.defaultProps = {
  sortOrder: 'default',
};

const GroupedQuestionRow = ({ row, viewMode, scaleLegend, scaleColors, metricColor }) => {
  const totalResponses = Number(row.n || 0);

  if (row.suppressed) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '31% 1fr 66px' },
          alignItems: 'center',
          columnGap: { xs: 1.4, md: 1.6 },
          rowGap: { xs: 1.1, md: 0.7 },
          py: { xs: 1.3, md: 1.45 },
          px: 2.0,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 400, color: '#475569', fontSize: '1rem', lineHeight: 1.25 }}>
          {row.group_value}
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.875rem', lineHeight: 1.4 }}>
          {ANONYMITY_MESSAGE}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <Typography variant="caption" sx={{ ...META_TEXT_SX, letterSpacing: 0, fontWeight: 400 }}>
            {formatCount(totalResponses)}
          </Typography>
        </Box>
      </Box>
    );
  }

  const average = Number(row.average || 0);
  const percentPositive = Number(row.percent_positive || 0);
  const averagePercent = averageToPercent(average, totalResponses);
  const hasResponses = totalResponses > 0;
  const isDistributionView = viewMode === 'distribution';
  const groupedRowMetricValue = !hasResponses ? '—' : viewMode === 'percent_positive' ? formatPercent(percentPositive) : formatAverage(average);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '31% 1fr 66px' },
        alignItems: 'center',
        columnGap: { xs: 1.4, md: 1.6 },
        rowGap: { xs: 1.1, md: 0.7 },
        py: { xs: 1.3, md: 1.45 },
        px: 2.0,
        borderTop: '1px solid #e2e8f0',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 400,
          color: '#475569',
          fontSize: { xs: '1rem', md: '1rem' },
          lineHeight: 1.25,
        }}
      >
        {row.group_value}
      </Typography>
      <Box>
        {viewMode === 'distribution' && <DistributionBar distribution={row.distribution} scaleColors={scaleColors} scaleLegend={scaleLegend} />}
        {viewMode === 'percent_positive' && <MetricBar percent={hasResponses ? percentPositive : 0} metricColor={metricColor} />}
        {viewMode === 'average' && <MetricBar percent={hasResponses ? averagePercent : 0} metricColor={metricColor} />}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
        {isDistributionView ? (
          <Typography
            variant="caption"
            sx={{
              ...META_TEXT_SX,
              letterSpacing: 0,
              textAlign: { xs: 'left', md: 'right' },
              fontWeight: 400,
              mr: { xs: 0, md: '6px' },
            }}
          >
            {formatCount(totalResponses)}
          </Typography>
        ) : hasResponses ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
            <Typography
              variant="body2"
              sx={{
                minWidth: 48,
                px: 1.0,
                py: 0.2,
                textAlign: 'center',
                borderRadius: 2,
                backgroundColor: '#e2e8f0',
                color: '#334155',
                fontWeight: 700,
                fontSize: '1.05rem',
                lineHeight: 1.25,
                letterSpacing: 0.3,
              }}
            >
              {groupedRowMetricValue}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                ...META_TEXT_SX,
                letterSpacing: 0,
                fontWeight: 400,
                mt: 0.35,
              }}
            >
              {formatCount(totalResponses)}
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{
              ...META_TEXT_SX,
              letterSpacing: 0,
              textAlign: { xs: 'left', md: 'right' },
              fontWeight: 400,
            }}
          >
            {groupedRowMetricValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

GroupedQuestionRow.propTypes = {
  row: PropTypes.shape({
    group_value: PropTypes.string.isRequired,
    n: PropTypes.number,
    average: PropTypes.number,
    percent_positive: PropTypes.number,
    distribution: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  viewMode: PropTypes.oneOf(['average', 'percent_positive', 'distribution']).isRequired,
  scaleLegend: PropTypes.arrayOf(SCALE_LEGEND_ENTRY_PROP_TYPE),
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  metricColor: PropTypes.string.isRequired,
};

GroupedQuestionRow.defaultProps = {
  scaleLegend: [],
};

const GroupedQuestionCard = ({ question, viewMode, isFirstInDomain, sortOrder, scaleColors, metricColor }) => {
  const groupedHeaderMetric = question.summary
    ? resolveGroupedQuestionHeaderMetricFromSummary(question.summary, viewMode)
    : resolveGroupedQuestionHeaderMetric(question.rows, viewMode);
  const [expanded, setExpanded] = React.useState(true);
  const sortedRows = React.useMemo(() => sortRows(question.rows, sortOrder, viewMode), [question.rows, sortOrder, viewMode]);

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderTopLeftRadius: isFirstInDomain ? 0 : 2.2,
        borderTopRightRadius: isFirstInDomain ? 0 : 2.2,
        borderBottomLeftRadius: 2.2,
        borderBottomRightRadius: 2.2,
        overflow: 'hidden',
        mb: 3,
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        sx={{
          px: 2.2,
          py: 1.5,
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 1.2 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '1rem',
              lineHeight: 1.3,
            }}
          >
            {question.question}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexShrink: 0 }}>
            {groupedHeaderMetric && (
              <Typography
                variant="body2"
                sx={{
                  color: '#334155',
                  fontWeight: 400,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.3,
                  letterSpacing: 0,
                  flexShrink: 0,
                }}
              >
                <Box component="span" sx={{ fontSize: { xs: '0.825rem', md: '0.875rem' }, fontWeight: 400, mr: 0.7 }}>
                  {groupedHeaderMetric.label}
                </Box>
                <Box component="span" sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, fontWeight: 700, color: '#1e293b' }}>
                  {groupedHeaderMetric.value}
                </Box>
              </Typography>
            )}
            <KeyboardArrowDownIcon
              sx={{
                color: '#94a3b8',
                fontSize: 20,
                transform: expanded ? 'rotate(0deg)' : 'rotate(-180deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        </Stack>
      </Box>
      <Collapse in={expanded} unmountOnExit timeout={180}>
        <Box>
          {sortedRows.map((row) => (
            <GroupedQuestionRow
              key={`${question.question_id}-${row.group_value}`}
              row={row}
              viewMode={viewMode}
              scaleLegend={question.scale_legend}
              scaleColors={scaleColors}
              metricColor={metricColor}
            />
          ))}
          {question.scale_legend?.length > 0 && (
            <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
              <Legend scaleLegend={question.scale_legend} scaleColors={scaleColors} />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

GroupedQuestionCard.propTypes = {
  question: PropTypes.shape({
    question_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    question: PropTypes.string.isRequired,
    scale_legend: PropTypes.arrayOf(SCALE_LEGEND_ENTRY_PROP_TYPE),
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        group_value: PropTypes.string.isRequired,
        n: PropTypes.number,
        average: PropTypes.number,
        percent_positive: PropTypes.number,
        distribution: PropTypes.arrayOf(PropTypes.number),
      })
    ).isRequired,
  }).isRequired,
  viewMode: PropTypes.oneOf(['average', 'percent_positive', 'distribution']).isRequired,
  isFirstInDomain: PropTypes.bool,
  sortOrder: PropTypes.oneOf(['default', 'desc', 'asc']),
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  metricColor: PropTypes.string.isRequired,
};

GroupedQuestionCard.defaultProps = {
  isFirstInDomain: false,
  sortOrder: 'default',
};

const GroupedDomainSection = React.memo(({ domain, questions, viewMode, isFirst, questionOrderInDomain, sortOrder, scaleColors, metricColor }) => {
  const [expanded, setExpanded] = React.useState(true);
  const sortedQuestions = React.useMemo(
    () => sortGroupedQuestions(questions, sortOrder, viewMode, questionOrderInDomain),
    [questions, sortOrder, viewMode, questionOrderInDomain]
  );
  const domainHeaderMetric = resolveGroupedDomainHeaderMetric(sortedQuestions, viewMode);

  return (
    <Box sx={{ mt: isFirst ? 0 : 2.7 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        sx={{
          px: 2.5,
          py: 1.9,
          backgroundColor: PULSE_V2_NEUTRAL_SURFACE_BG,
          borderTopLeftRadius: '1rem',
          borderTopRightRadius: '1rem',
          borderBottomLeftRadius: expanded ? 0 : '1rem',
          borderBottomRightRadius: expanded ? 0 : '1rem',
          mb: expanded ? 1.15 : 0,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'border-radius 0.18s ease, margin-bottom 0.18s ease',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 1.2 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              fontSize: '1rem',
              lineHeight: 1.3,
            }}
          >
            {domain}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexShrink: 0 }}>
            {domainHeaderMetric && (
              <Typography
                variant="body2"
                sx={{
                  color: '#334155',
                  fontWeight: 400,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.3,
                  letterSpacing: 0,
                  flexShrink: 0,
                }}
              >
                <Box component="span" sx={{ fontSize: { xs: '0.825rem', md: '0.875rem' }, fontWeight: 400, mr: 0.7 }}>
                  Domain Overall
                </Box>
                <Box component="span" sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, fontWeight: 700, color: '#1e293b' }}>
                  {domainHeaderMetric.value}
                </Box>
              </Typography>
            )}
            <KeyboardArrowDownIcon
              sx={{
                color: '#94a3b8',
                fontSize: 20,
                transform: expanded ? 'rotate(0deg)' : 'rotate(-180deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        </Stack>
      </Box>
      <Collapse in={expanded} unmountOnExit timeout={180}>
        <Box>
          {sortedQuestions.map((question, questionIndex) => (
            <GroupedQuestionCard
              key={question.question_id || question.question}
              question={question}
              viewMode={viewMode}
              isFirstInDomain={questionIndex === 0}
              sortOrder={sortOrder}
              scaleColors={scaleColors}
              metricColor={metricColor}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
});

GroupedDomainSection.displayName = 'GroupedDomainSection';

GroupedDomainSection.propTypes = {
  domain: PropTypes.string.isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  viewMode: PropTypes.oneOf(['average', 'percent_positive', 'distribution']).isRequired,
  isFirst: PropTypes.bool,
  questionOrderInDomain: PropTypes.objectOf(PropTypes.number),
  sortOrder: PropTypes.oneOf(['default', 'desc', 'asc']),
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  metricColor: PropTypes.string.isRequired,
};

GroupedDomainSection.defaultProps = {
  isFirst: false,
  questionOrderInDomain: {},
  sortOrder: 'default',
};

const GroupedComparisonSection = ({
  groupedSection,
  viewMode,
  domainByQuestionId,
  domainOrder,
  questionOrderInDomain,
  sortOrder,
  scaleColors,
  metricColor,
}) => {
  const questionCards = groupedSection?.questions || [];
  const groupedQuestionCardsByDomain = questionCards.reduce((accumulator, question) => {
    const resolvedDomain = question?.domain || domainByQuestionId[`${question?.question_id ?? ''}`] || GROUPED_DOMAIN_FALLBACK;
    if (!accumulator[resolvedDomain]) {
      accumulator[resolvedDomain] = [];
    }
    accumulator[resolvedDomain].push(question);
    return accumulator;
  }, {});
  const groupedQuestionDomains = Object.entries(groupedQuestionCardsByDomain).sort(([a, aQuestions], [b, bQuestions]) => {
    if (sortOrder !== 'default') {
      const direction = sortOrder === 'desc' ? -1 : 1;
      return direction * (getGroupedDomainSortValue(aQuestions, viewMode) - getGroupedDomainSortValue(bQuestions, viewMode));
    }
    const ai = domainOrder.indexOf(a);
    const bi = domainOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return (
    <Box sx={{ mt: 3 }}>
      {questionCards.length > 0 ? (
        groupedQuestionDomains.map(([domain, questions], index) => (
          <GroupedDomainSection
            key={domain}
            domain={domain}
            questions={questions}
            viewMode={viewMode}
            isFirst={index === 0}
            questionOrderInDomain={questionOrderInDomain}
            sortOrder={sortOrder}
            scaleColors={scaleColors}
            metricColor={metricColor}
          />
        ))
      ) : (
        <LikertEmptyState message="No responses found for the selected filters." />
      )}
    </Box>
  );
};

GroupedComparisonSection.propTypes = {
  groupedSection: PropTypes.shape({
    title: PropTypes.string,
    questions: PropTypes.arrayOf(PropTypes.object),
  }),
  viewMode: PropTypes.oneOf(['average', 'percent_positive', 'distribution']).isRequired,
  domainByQuestionId: PropTypes.objectOf(PropTypes.string),
  domainOrder: PropTypes.arrayOf(PropTypes.string),
  questionOrderInDomain: PropTypes.objectOf(PropTypes.number),
  sortOrder: PropTypes.oneOf(['default', 'desc', 'asc']),
  scaleColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  metricColor: PropTypes.string.isRequired,
};

GroupedComparisonSection.defaultProps = {
  groupedSection: null,
  domainByQuestionId: {},
  domainOrder: [],
  questionOrderInDomain: {},
  sortOrder: 'default',
};

const LikertSection = ({ sections, groupedSection, groupBy, viewMode, onViewModeChange, sortOrder, themeKey }) => {
  const activeTheme = THEMES[themeKey] || THEMES[DEFAULT_THEME_KEY];
  const scaleColors = activeTheme.scale;
  const metricColor = activeTheme.metric;
  const hasSections = sections.length > 0;
  const normalizedGroupBy = groupBy || 'none';
  const isGroupedMode = normalizedGroupBy === 'school' || normalizedGroupBy === 'role';
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const targetGroupModeViewKey = isGroupedMode ? 'grouped' : 'ungrouped';
  const [displayedGroupModeViewKey, setDisplayedGroupModeViewKey] = React.useState(targetGroupModeViewKey);
  const [isGroupModeContentVisible, setIsGroupModeContentVisible] = React.useState(true);
  const groupModeTransitionTimerRef = React.useRef(null);
  const domainByQuestionId = sections.reduce((accumulator, section) => {
    (section?.questions || []).forEach((question) => {
      const questionId = question?.question_id;
      if (questionId === null || questionId === undefined) {
        return;
      }
      accumulator[`${questionId}`] = section?.domain || GROUPED_DOMAIN_FALLBACK;
    });
    return accumulator;
  }, {});
  const domainOrder = sections.map((s) => s.domain);
  const questionOrderInDomain = sections.reduce((acc, section) => {
    (section?.questions || []).forEach((question, index) => {
      const questionId = question?.question_id;
      if (questionId !== null && questionId !== undefined) {
        acc[`${questionId}`] = index;
      }
    });
    return acc;
  }, {});

  React.useEffect(() => {
    if (groupModeTransitionTimerRef.current) {
      clearTimeout(groupModeTransitionTimerRef.current);
      groupModeTransitionTimerRef.current = null;
    }

    if (prefersReducedMotion) {
      setDisplayedGroupModeViewKey(targetGroupModeViewKey);
      setIsGroupModeContentVisible(true);
      return undefined;
    }

    if (targetGroupModeViewKey === displayedGroupModeViewKey) {
      setIsGroupModeContentVisible(true);
      return undefined;
    }

    setIsGroupModeContentVisible(false);
    groupModeTransitionTimerRef.current = setTimeout(() => {
      setDisplayedGroupModeViewKey(targetGroupModeViewKey);
      setIsGroupModeContentVisible(true);
      groupModeTransitionTimerRef.current = null;
    }, GROUP_MODE_TRANSITION_EXIT_MS);

    return () => {
      if (groupModeTransitionTimerRef.current) {
        clearTimeout(groupModeTransitionTimerRef.current);
        groupModeTransitionTimerRef.current = null;
      }
    };
  }, [displayedGroupModeViewKey, prefersReducedMotion, targetGroupModeViewKey]);

  const isDisplayedGroupedMode = displayedGroupModeViewKey === 'grouped';
  const sortedSections = React.useMemo(
    () => (viewMode === 'distribution' ? sections : sortUngroupedSections(sections, sortOrder, viewMode)),
    [sections, sortOrder, viewMode]
  );
  const ungroupedContent = hasSections ? (
    sortedSections.map((section) => (
      <DomainCard
        key={section.domain}
        section={section}
        viewMode={viewMode}
        sortOrder={sortOrder}
        scaleColors={scaleColors}
        metricColor={metricColor}
      />
    ))
  ) : (
    <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 2.2, backgroundColor: '#ffffff', p: 3 }}>
      <Typography variant="body2" sx={{ color: '#64748b' }}>
        No Likert responses are available for the selected filters.
      </Typography>
    </Box>
  );
  const groupModeContent = isDisplayedGroupedMode ? (
    <GroupedComparisonSection
      groupedSection={groupedSection}
      viewMode={viewMode}
      domainByQuestionId={domainByQuestionId}
      domainOrder={domainOrder}
      questionOrderInDomain={questionOrderInDomain}
      sortOrder={sortOrder}
      scaleColors={scaleColors}
      metricColor={metricColor}
    />
  ) : (
    ungroupedContent
  );
  const groupModeTransitionTimeout = prefersReducedMotion ? 0 : { enter: GROUP_MODE_TRANSITION_ENTER_MS, exit: GROUP_MODE_TRANSITION_EXIT_MS };
  const groupModeTransitionEasing = prefersReducedMotion ? undefined : { enter: GROUP_MODE_TRANSITION_EASING, exit: GROUP_MODE_TRANSITION_EASING };

  return (
    <Box sx={{ mt: 0.2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" className="mb-3">
        <Typography variant="body1" sx={{ color: '#637a9a', letterSpacing: 0.45, fontWeight: 700, textTransform: 'uppercase' }}>
          Likert Scale Questions
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_, nextMode) => nextMode && onViewModeChange(nextMode)}
          sx={WHITE_PILL_SEGMENTED_TOGGLE_SX}
        >
          {VIEW_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value} disableRipple>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Fade key={displayedGroupModeViewKey} in={isGroupModeContentVisible} timeout={groupModeTransitionTimeout} easing={groupModeTransitionEasing}>
        <Box sx={{ willChange: 'opacity' }}>{groupModeContent}</Box>
      </Fade>
    </Box>
  );
};

LikertSection.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      domain: PropTypes.string.isRequired,
      summary: PropTypes.shape({
        average: PropTypes.number,
        percent_positive: PropTypes.number,
        total_responses: PropTypes.number,
      }),
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          question_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          question: PropTypes.string,
          base: PropTypes.shape({
            distribution: PropTypes.arrayOf(PropTypes.number),
            total_responses: PropTypes.number,
            average: PropTypes.number,
            percent_positive: PropTypes.number,
            scale_legend: PropTypes.arrayOf(SCALE_LEGEND_ENTRY_PROP_TYPE),
          }),
          comparison: PropTypes.object,
          deltas: PropTypes.shape({
            average_change: PropTypes.number,
            percent_positive_change: PropTypes.number,
          }),
        })
      ).isRequired,
    })
  ).isRequired,
  groupedSection: PropTypes.shape({
    title: PropTypes.string,
    questions: PropTypes.arrayOf(PropTypes.object),
  }),
  groupBy: PropTypes.oneOf(['none', 'school', 'role']),
  viewMode: PropTypes.oneOf(['average', 'percent_positive', 'distribution']).isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  sortOrder: PropTypes.oneOf(['default', 'desc', 'asc']),
  themeKey: PropTypes.string,
};

LikertSection.defaultProps = {
  groupedSection: null,
  groupBy: 'none',
  sortOrder: 'default',
  themeKey: DEFAULT_THEME_KEY,
};

export default LikertSection;
